import { useCallback, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import {
  ALLOWED_LOGO_CONTENT_TYPES,
  MAX_LOGO_BYTES,
  MAX_LOGO_DIMENSION,
} from './types';

export interface UploadResult {
  s3_key?: string;
  preview_url?: string;
  error?: { code: string; message: string };
}

const SVG_SCRIPT_RE = /<script[\s>]/i;

async function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    if (file.type === 'image/svg+xml') {
      // Skip dimension check for SVG — vector, no fixed pixel dims
      resolve({ width: 0, height: 0 });
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to read image dimensions'));
    };
    img.src = url;
  });
}

async function readSvgText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read SVG'));
    reader.readAsText(file);
  });
}

async function validateClientSide(file: File): Promise<string | null> {
  if (!ALLOWED_LOGO_CONTENT_TYPES.includes(file.type as (typeof ALLOWED_LOGO_CONTENT_TYPES)[number])) {
    return `Unsupported file type. Use PNG, JPG, or SVG.`;
  }
  if (file.size > MAX_LOGO_BYTES) {
    return `File is too large (${Math.round(file.size / 1024)}KB). Max 500KB.`;
  }
  if (file.size === 0) {
    return 'File is empty.';
  }

  if (file.type === 'image/svg+xml') {
    try {
      const text = await readSvgText(file);
      if (SVG_SCRIPT_RE.test(text)) {
        return 'SVG contains <script> tag — rejected for safety.';
      }
    } catch {
      return 'Could not read SVG contents.';
    }
    return null;
  }

  try {
    const { width, height } = await readImageDimensions(file);
    if (width > MAX_LOGO_DIMENSION || height > MAX_LOGO_DIMENSION) {
      return `Image dimensions ${width}×${height}px exceed ${MAX_LOGO_DIMENSION}px max.`;
    }
  } catch (err) {
    return err instanceof Error ? err.message : 'Image read failed.';
  }
  return null;
}

export function useUploadQrLogo(): {
  upload: (file: File) => Promise<UploadResult>;
  loading: boolean;
} {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(false);

  const upload = useCallback(
    async (file: File): Promise<UploadResult> => {
      if (!isAuthenticated || accounts.length === 0) {
        return { error: { code: 'not_authenticated', message: 'Sign in to upload logos' } };
      }
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        return { error: { code: 'config_missing', message: 'REACT_APP_API_URL is not set' } };
      }

      const validationError = await validateClientSide(file);
      if (validationError) {
        return { error: { code: 'invalid_file', message: validationError } };
      }

      setLoading(true);
      try {
        const tokenResponse = { accessToken: await acquireApiToken(instance, accounts[0]) };
        const headers = {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json',
        };

        // Step 1: ask backend for a presigned PUT URL
        const presignRes = await fetch(`${apiUrl}/admin/qr-codes/upload-logo`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            content_type: file.type,
            size: file.size,
            extension: extensionFor(file),
          }),
        });
        const presignJson = await presignRes.json();
        if (!presignRes.ok) {
          return {
            error: {
              code: presignJson.error || `http_${presignRes.status}`,
              message: presignJson.error || `HTTP ${presignRes.status}`,
            },
          };
        }
        const { s3_key, upload_url } = presignJson.data;

        // Step 2: PUT the file directly to S3 with the presigned URL
        const putRes = await fetch(upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        if (!putRes.ok) {
          return {
            error: {
              code: 'upload_failed',
              message: `S3 upload failed (HTTP ${putRes.status})`,
            },
          };
        }

        // Local blob URL for instant preview while the row is being saved
        const previewUrl = URL.createObjectURL(file);
        return { s3_key, preview_url: previewUrl };
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Upload failed';
        return { error: { code: 'network_error', message: msg } };
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, instance, accounts],
  );

  return { upload, loading };
}

function extensionFor(file: File): string {
  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/jpeg') return 'jpg';
  if (file.type === 'image/svg+xml') return 'svg';
  return 'bin';
}
